import { useTranslation } from "react-i18next";
import { X, Dices, RotateCcw, CheckCircle2, Trophy, MinusCircle, Sparkles, BookOpen, Info, Ban } from "lucide-react";

interface Props {
  onClose: () => void;
}

export default function RulesModal({ onClose }: Props) {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-950/80 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="glass-modal w-full max-w-lg max-h-[85dvh] rounded-3xl flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink/10 glass-header sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600/20 flex items-center justify-center text-blue-400">
              <BookOpen size={22} />
            </div>
            <h2 className="text-xl font-bold">{t("rules.title")}</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-ink/10 rounded-full text-ink/50 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8 pb-6 custom-scrollbar">
          
          {/* Overview */}
          <section className="space-y-3">
            <div className="flex items-center gap-2 text-blue-400 font-bold uppercase text-xs tracking-widest">
              <Info size={14} /> {t("rules.overview")}
            </div>
            <p className="text-ink/70 leading-relaxed text-sm">
              {t("rules.overviewDesc")}
            </p>
          </section>

          {/* Turn Flow */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 text-purple-400 font-bold uppercase text-xs tracking-widest">
              <RotateCcw size={14} /> {t("rules.turnFlow")}
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="flex gap-4 p-4 bg-surface-2 rounded-2xl border border-gray-700/30">
                <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center font-bold shrink-0 text-sm text-white">1</div>
                <div>
                  <h4 className="font-bold text-sm mb-1">{t("rules.step1Title")}</h4>
                  <p className="text-xs text-ink/50 leading-normal">
                    {t("rules.step1Desc")}
                  </p>
                </div>
              </div>
              <div className="flex gap-4 p-4 bg-surface-2 rounded-2xl border border-gray-700/30">
                <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center font-bold shrink-0 text-sm text-white">2</div>
                <div>
                  <h4 className="font-bold text-sm mb-1">{t("rules.step2Title")}</h4>
                  <p className="text-xs text-ink/50 leading-normal">
                    {t("rules.step2Desc")}
                  </p>
                </div>
              </div>
              <div className="flex gap-4 p-4 bg-blue-500/10 rounded-2xl border border-blue-500/20">
                <div className="w-8 h-8 rounded-lg bg-blue-400 flex items-center justify-center font-bold shrink-0 text-white"><Sparkles size={16} /></div>
                <div>
                  <h4 className="font-bold text-sm mb-1 text-blue-300">{t("rules.bonusTitle")}</h4>
                  <p className="text-xs text-blue-300/70 leading-normal">
                    {t("rules.bonusDesc")}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* School Section */}
          <section className="space-y-3">
            <div className="flex items-center gap-2 text-emerald-400 font-bold uppercase text-xs tracking-widest">
              <Trophy size={14} /> {t("rules.schoolTitle")}
            </div>
            <p className="text-xs text-ink/50 mb-2">
              {t("rules.schoolDesc")}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 bg-surface-2 rounded-xl border border-gray-700/30 text-center">
                <div className="text-lg mb-1">🎲🎲🎲🎲</div>
                <div className="text-xs font-bold text-emerald-400">{t("rules.ptsOnSixes")}</div>
              </div>
              <div className="p-3 bg-surface-2 rounded-xl border border-gray-700/30 text-center">
                <div className="text-lg mb-1">🎲🎲</div>
                <div className="text-xs font-bold text-rose-400">{t("rules.ptsPenaltyOnSixes")}</div>
              </div>
            </div>
            <div className="bg-surface-2 p-3 rounded-xl border border-gray-700/20">
              <h5 className="text-[10px] font-bold text-ink/40 uppercase mb-2">{t("rules.schoolBonuses")}</h5>
              <ul className="text-xs space-y-2">
                <li className="flex items-start gap-2 text-ink/50">
                  <CheckCircle2 size={12} className="text-emerald-500 mt-0.5 shrink-0" />
                  <span>{t("rules.allRowsPositive")}</span>
                </li>
                <li className="flex items-start gap-2 text-ink/50">
                  <CheckCircle2 size={12} className="text-blue-500 mt-0.5 shrink-0" />
                  <span>{t("rules.allRowsZeroed")}</span>
                </li>
                <li className="flex items-start gap-2 text-ink/50">
                  <MinusCircle size={12} className="text-rose-500 mt-0.5 shrink-0" />
                  <span>{t("rules.negativePenalty")}</span>
                </li>
              </ul>
              <div className="mt-3 pt-3 border-t border-gray-700/30 text-[10px] text-ink/40 italic">
                {t("rules.restrictionHint")}
              </div>
            </div>
          </section>

          {/* Combinations Section */}
          <section className="space-y-3">
            <div className="flex items-center gap-2 text-amber-400 font-bold uppercase text-xs tracking-widest">
              <Dices size={14} /> {t("rules.combosTitle")}
            </div>
            <div className="space-y-2">
              {[
                { name: t("rules.onePair"),      desc: t("rules.onePairDesc") },
                { name: t("rules.twoPairs"),     desc: t("rules.twoPairsDesc") },
                { name: t("rules.threeOfKind"),  desc: t("rules.threeOfKindDesc") },
                { name: t("rules.straight"),     desc: t("rules.straightDesc") },
                { name: t("rules.fullHouse"),    desc: t("rules.fullHouseDesc") },
                { name: t("categories.four_of_kind") + " / " + t("categories.poker"), desc: t("rules.pokerDesc") },
                { name: t("rules.garbageTitle"), desc: t("rules.garbageDesc") },
              ].map((c, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-surface-2 rounded-xl border border-gray-700/30">
                  <div>
                    <div className="text-xs font-bold text-ink">{c.name}</div>
                    <div className="text-[10px] text-ink/40">{c.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 bg-amber-500/10 rounded-2xl border border-amber-500/20 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 shrink-0">
                <Ban size={20} />
              </div>
              <p className="text-xs text-amber-200/80 leading-tight">
                {t("rules.mustCrossOut")}
              </p>
            </div>
          </section>

        </div>
        
        {/* Footer */}
        <div className="p-4 bg-surface-2 border-t border-gray-800">
          <button 
            onClick={onClose}
            className="w-full py-4 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 rounded-2xl font-bold text-white transition-all shadow-lg shadow-blue-600/20"
          >
            {t("rules.gotIt")}
          </button>
        </div>

      </div>
    </div>
  );
}
